import React from 'react';
import { X, TrendingUp, Clock, DollarSign, Users, Target, Award } from 'lucide-react';
import { Modal } from './ui/Modal';
import { formatCurrency, formatPercent } from '../utils/format';

interface ReportData {
  totalExpectedRevenue: number;
  totalReceivedRevenue: number;
  totalExpectedExpenses: number;
  totalPaidExpenses: number;
  totalCollabFeeExpected: number;
  totalCollabFeePaid: number;
  totalCollabFeePending: number;
  marginExpected: number;
  marginReal: number;
  toReceive: number;
  toPay: number;
  profitPct: number;
  roi: number;
  estimatedHours: number;
  hourlyRate: number;
  breakEvenRevenue: number;
  breakEvenPct: number;
  breakEvenReached: boolean;
  profitPerGuest?: number;
  daysUntilEvent?: number;
}

interface FinancialReportModalProps {
  open: boolean;
  onClose: () => void;
  data: ReportData | null;
  loading: boolean;
}

const MetricCard = ({ icon, label, value, subValue, color }: { icon: React.ReactNode; label: string; value: string; subValue?: string; color: string }) => (
  <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
      <span style={{ color, opacity: 0.85 }}>{icon}</span>
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>{label}</span>
    </div>
    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {subValue && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.125rem' }}>{subValue}</div>}
  </div>
);

export function FinancialReportModal({ open, onClose, data, loading }: FinancialReportModalProps) {
  if (!data || loading) return null;

  const d = data;
  const roi = d.roi;
  const roiColor = roi >= 30 ? 'var(--color-success)' : roi >= 10 ? 'var(--color-warning)' : 'var(--color-error)';
  const marginColor = d.profitPct >= 20 ? 'var(--color-success)' : d.profitPct >= 5 ? 'var(--color-warning)' : 'var(--color-error)';
  const breakevenColor = d.breakEvenReached ? 'var(--color-success)' : 'var(--color-warning)';

  return (
    <Modal open={open} onClose={onClose} title="Report Finanziario Avanzato" width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Top-row KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <MetricCard icon={<TrendingUp size={16} />} label="ROI (Ritorno Investimento)" value={formatPercent(roi)} subValue={`Margine / Costi totali × 100`} color={roiColor} />
          <MetricCard icon={<Award size={16} />} label="Margine %" value={formatPercent(d.profitPct)} subValue={`${d.profitPct >= 20 ? 'Ottimo' : d.profitPct >= 5 ? 'Buono' : 'Basso'}`} color={marginColor} />
          <MetricCard icon={<Target size={16} />} label="Break-even" value={`${formatPercent(d.breakEvenPct)} ricavi`} subValue={d.breakEvenReached ? '✓ Raggiunto' : `Mancano ${formatCurrency(d.breakEvenRevenue - d.totalReceivedRevenue)}`} color={breakevenColor} />
        </div>

        {/* Hourly rate */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
          <MetricCard icon={<Clock size={16} />} label="Costo/ora stimato" value={formatCurrency(d.hourlyRate)} subValue={`${d.estimatedHours}h stimate`} color="var(--color-primary)" />
          <MetricCard icon={<DollarSign size={16} />} label="Ricavo netto" value={formatCurrency(d.totalExpectedRevenue)} subValue={`Incassato: ${formatCurrency(d.totalReceivedRevenue)}`} color="var(--color-primary)" />
          <MetricCard icon={<Users size={16} />} label="Costo collaboratori" value={formatCurrency(d.totalCollabFeeExpected)} subValue={`Da pagare: ${formatCurrency(d.totalCollabFeePending)}`} color="var(--color-gold)" />
        </div>

        {/* Per-guest analysis (if applicable) */}
        {d.profitPerGuest !== undefined && d.profitPerGuest !== 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <MetricCard icon={<Users size={16} />} label="Profit per ospite" value={formatCurrency(d.profitPerGuest)} subValue="Utile / ospiti stimati" color="var(--color-primary)" />
            <MetricCard icon={<Users size={16} />} label="Ospeti stimati" value={String(Math.round(d.totalExpectedRevenue / 15))} subValue="Basato su location" color="var(--color-text-muted)" />
          </div>
        )}

        {/* Timeline */}
        {d.daysUntilEvent !== undefined && (
          <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1rem' }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: '0.5rem' }}>Timeline</p>
            <p style={{ fontSize: 'var(--text-sm)', color: d.daysUntilEvent > 30 ? 'var(--color-success)' : d.daysUntilEvent > 14 ? 'var(--color-warning)' : 'var(--color-error)' }}>
              {d.daysUntilEvent > 0
                ? `${d.daysUntilEvent} giorni all'evento`
                : d.daysUntilEvent === 0 ? "Oggi è l'evento!" : `Evento passato (${Math.abs(d.daysUntilEvent)} giorni fa)`}
            </p>
          </div>
        )}

        {/* Detailed P&L table */}
        <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.10)', background: 'var(--glass-bg-card)' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: 600, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Voce</th>
                <th style={{ textAlign: 'right', padding: '0.75rem 1rem', fontWeight: 600, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Atteso</th>
                <th style={{ textAlign: 'right', padding: '0.75rem 1rem', fontWeight: 600, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Reale</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ padding: '0.625rem 1rem' }}>Ricavi</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right', color: 'var(--color-success)' }}>{formatCurrency(d.totalExpectedRevenue)}</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right' }}>{formatCurrency(d.totalReceivedRevenue)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ padding: '0.625rem 1rem' }}>Spese dirette</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right', color: 'var(--color-error)' }}>-{formatCurrency(d.totalExpectedExpenses)}</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right' }}>-{formatCurrency(d.totalPaidExpenses)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ padding: '0.625rem 1rem' }}>Collaboratori</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right', color: 'var(--color-gold)' }}>-{formatCurrency(d.totalCollabFeeExpected)}</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right' }}>-{formatCurrency(d.totalCollabFeePaid)}</td>
              </tr>
              <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.10)', fontWeight: 600 }}>
                <td style={{ padding: '0.625rem 1rem' }}>Utile netto</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right', color: d.marginExpected >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>{formatCurrency(d.marginExpected)}</td>
                <td style={{ padding: '0.625rem 1rem', textAlign: 'right', color: d.marginReal >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>{formatCurrency(d.marginReal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
